import React, { useMemo, useState, useEffect } from "react";
import PropTypes from "prop-types";
import moment from "moment";
import { Calendar, Views, momentLocalizer } from "react-big-calendar";
import "react-big-calendar/lib/css/react-big-calendar.css";
import "config/css/react-big-calendar-overrides.css";
import { injectIntl } from "react-intl";
import styles from "./styles/event-agenda.css.js";
import withStyles from "components/with-styles";
import { getEventAgendaQuery } from "./event-agenda-query";
import { useQuery } from "components/graphql";
import { useCurrentCustomerContext } from "components/current-customer-context";
import { useLoadingContext } from "components/loading-spinner-overlay";
import { withRouter } from "react-router-dom";
import { isMobile } from "react-device-detect";
import { useImperativeTracking } from "components/tracking";
import { onChangeParams, parseParams } from "components/table";
import { useAuthDataContext } from "components/auth";
import Checkbox from "@material-ui/core/Checkbox";
import { RestrictedToAdmin } from "components/auth";
import FormControlLabel from "@material-ui/core/FormControlLabel";

const getClassNameByDate = ({ classes }) => ({ startDate, endDate }) => {
  const now = moment();

  // CURRENT EVENT
  if (now.isBetween(moment(startDate), moment(endDate))) {
    return { className: classes.activeEvent };
  }

  // PAST EVENT
  if (moment(endDate).isBefore(now)) {
    return { className: classes.pastEvent };
  }

  // FUTURE EVENT
  return { className: classes.futureEvent };
};

const parseQueryParams = ({ search }) => {
  const queryParams = parseParams({ queryParams: search });
  const dateParam = queryParams["date"] ? moment(Number(queryParams["date"])) : moment();

  return [dateParam, queryParams["view"] || Views.WEEK];
};

const onChangeDateOrView = ({ setDate, moment, view, query, history }) => {
  setDate(moment);
  onChangeParams({
    paramsObject: {
      date: moment.valueOf(),
      view,
    },
    query,
    history,
  });
};

const EventAgenda = ({
  history,
  classes,
  intl,
  views = isMobile ? [Views.DAY] : [Views.WEEK, Views.DAY],
  defaultView = isMobile ? Views.DAY : Views.WEEK,
  location: { search },
}) => {
  const [showDeleted, setShowDeleted] = useState(false);
  const [queryDate, queryView] = parseQueryParams({ search });
  const [date, setDate] = useState(queryDate);
  const { currentCustomerId } = useCurrentCustomerContext();
  const { setLoading } = useLoadingContext();
  const track = useImperativeTracking();
  const { hasAdminPrivileges } = useAuthDataContext();

  const query = useMemo(
    () =>
      getEventAgendaQuery({
        hasAdminPrivileges,
        customerId: currentCustomerId,
        startDate: moment(date)
          .startOf("isoweek")
          .toDate(),
        endDate: moment(date)
          .endOf("isoweek")
          .toDate(),
        showDeleted,
      }),
    [currentCustomerId, date, hasAdminPrivileges, showDeleted],
  );

  const { data, loading } = useQuery(query);
  const events = data && data.events ? data.events.items : [];

  useEffect(
    () => {
      setLoading(loading);
    },
    [loading, setLoading],
  );

  const handleChangeDate = (date, view, action) => {
    track("moves to " + (action !== "TODAY" ? action : "") + view);
    setLoading(true);
    onChangeDateOrView({ setDate, moment: moment(date), view, query: search, history });
  };

  const handleChangeView = view => {
    track("changes to " + view + " view");
    onChangeDateOrView({ setDate, moment: moment(), view, query: search, history });
  };

  return (
    <>
      <RestrictedToAdmin>
        <FormControlLabel
          label={intl.formatMessage({ id: "event.list.showDeleted" })}
          control={
            <Checkbox
              id="showDeleted"
              name="showDeleted"
              value="showDeleted"
              checked={showDeleted || false}
              onChange={event => setShowDeleted(event.target.checked)}
              label={intl.formatMessage({ id: "event.list.showDeleted" })}
            />
          }
        />
      </RestrictedToAdmin>
      <Calendar
        className={classes.eventAgenda}
        showMultiDayTimes
        dayLayoutAlgorithm="no-overlap"
        drilldownView="null"
        tooltipAccessor={event => `[${event.contract ? event.contract.description : ""}]: ${event.title}`}
        resourceIdAccessor={event => event.id}
        resourceTitleAccessor={event => event.title}
        allDayAccessor={event => false}
        startAccessor={event => new Date(event.startDate)}
        endAccessor={event => new Date(event.endDate)}
        defaultView={defaultView}
        localizer={momentLocalizer(moment)}
        events={events}
        onSelectEvent={event => history.push(`/event/details/${event.id}/customer/${event.customerId}`)}
        eventPropGetter={getClassNameByDate({ classes })}
        views={views}
        view={queryView}
        onView={handleChangeView}
        date={date.toDate()}
        onNavigate={handleChangeDate}
        messages={{
          today: intl.formatMessage({ id: "app.agenda.today" }),
          previous: intl.formatMessage({ id: "app.agenda.previous" }),
          next: intl.formatMessage({ id: "app.agenda.next" }),
          week: intl.formatMessage({ id: "app.agenda.mode.week" }),
          day: intl.formatMessage({ id: "app.agenda.mode.day" }),
        }}
      />
    </>
  );
};

EventAgenda.propTypes = {
  /**
   * Array con las vistas permitidas en el calendario
   */
  views: PropTypes.array,
  /**
   * Vista por defecto en el calendario
   */
  defaultView: PropTypes.string,
};

export default withStyles(styles)(injectIntl(withRouter(EventAgenda)));
